import React from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  Box,
  Typography,
  Chip,
  LinearProgress,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { RunSummary, RunStatus } from '../../types/bidRecommendations';

interface RunCardProps {
  run: RunSummary;
  onClick: () => void;
}

const statusLabel: Record<RunStatus, string> = {
  pending_review: 'Pending Review',
  partially_reviewed: 'In Progress',
  fully_reviewed: 'Fully Reviewed',
};

const statusColor: Record<RunStatus, 'warning' | 'info' | 'success'> = {
  pending_review: 'warning',
  partially_reviewed: 'info',
  fully_reviewed: 'success',
};

const RunCard: React.FC<RunCardProps> = ({ run, onClick }) => {
  const progress =
    run.total_recommendations > 0
      ? (run.reviewed_count / run.total_recommendations) * 100
      : 0;

  return (
    <Card sx={{ mb: 1.5 }}>
      <CardActionArea onClick={onClick}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              {new Date(run.created_at).toLocaleString()}
            </Typography>
            <Chip
              label={statusLabel[run.status]}
              color={statusColor[run.status]}
              size="small"
            />
          </Box>

          <Box display="flex" gap={2} mb={1.5} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={0.5}>
              <TrendingUpIcon fontSize="small" color="success" />
              <Typography variant="body2">
                {run.increases} increase{run.increases !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <TrendingDownIcon fontSize="small" color="error" />
              <Typography variant="body2">
                {run.decreases} decrease{run.decreases !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <PauseCircleOutlineIcon fontSize="small" color="disabled" />
              <Typography variant="body2">
                {run.holds} hold{run.holds !== 1 ? 's' : ''}
              </Typography>
            </Box>
            {run.manual_reviews > 0 && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <RateReviewIcon fontSize="small" color="warning" />
                <Typography variant="body2">
                  {run.manual_reviews} manual review
                  {run.manual_reviews !== 1 ? 's' : ''}
                </Typography>
              </Box>
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
            />
            <Typography variant="caption" color="text.secondary" noWrap>
              {run.reviewed_count}/{run.total_recommendations} reviewed
            </Typography>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default RunCard;
